import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Car, Loader2, MessageSquareText } from 'lucide-react';
import {
  fetchHistory,
  fetchLearningBooklet,
  fetchLivret,
  setLearningSelfAssessment,
} from '../api/candidate';
import type { LearningBooklet } from '../types';
import { monitorInitials } from '../lib/format';
import { Photo } from '../components/Photos';
import dayjs from '../lib/dates';

// Niveaux d'auto-évaluation de l'élève (smileys).
const SELF_LEVELS = [
  { value: 1, icon: '🙁', label: 'Peu sûr' },
  { value: 2, icon: '😐', label: 'À confirmer' },
  { value: 3, icon: '🙂', label: 'À l\'aise' },
] as const;

// Progression posée par le staff / le moniteur (lecture seule).
const PROGRESSION_LABEL: Record<string, string> = {
  aborde: 'Abordé',
  traite: 'Traité',
  assimile: 'Assimilé',
};

// Progression de l'auto-évaluation de l'élève (côté candidat), pondérée sur 3
// niveaux — c'est cette progression qui avance quand l'élève se note.
const SELF_WEIGHT: Record<number, number> = { 1: 1 / 3, 2: 2 / 3, 3: 1 };
function selfPct(items: { selfAssessment: number | null }[]): number {
  if (items.length === 0) return 0;
  const sum = items.reduce(
    (acc, it) => acc + (it.selfAssessment ? SELF_WEIGHT[it.selfAssessment] : 0),
    0,
  );
  return Math.round((sum / items.length) * 100);
}

function LearningBookletCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'learning-booklet'],
    queryFn: fetchLearningBooklet,
  });

  const mutation = useMutation({
    mutationFn: (vars: { itemCode: string; level: number | null }) =>
      setLearningSelfAssessment(vars.itemCode, vars.level),
    onSuccess: (fresh: LearningBooklet) => {
      queryClient.setQueryData(['candidate', 'learning-booklet'], fresh);
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center text-slate-500 text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Chargement du livret…
      </div>
    );
  }
  if (isError || !data) return null;

  const overallSelfPct = selfPct(data.categories.flatMap((c) => c.items));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-slate-900">Livret d'apprentissage</h2>
          <p className="text-xs text-slate-500">{data.referentialLabel}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-extrabold text-indigo-600 leading-none">
            {overallSelfPct}%
          </div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">
            Progression
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Auto-évaluez chaque compétence. Votre moniteur voit vos réponses.
      </p>

      <div className="space-y-5">
        {data.categories.map((cat) => {
          const catPct = selfPct(cat.items);
          return (
          <div
            key={cat.code}
            className="border border-slate-200 rounded-xl overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800 flex items-baseline gap-2 min-w-0">
                <span className="text-indigo-600 font-extrabold">{cat.code}</span>
                <span className="truncate">{cat.title}</span>
              </h3>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-20 h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${catPct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-indigo-600 w-9 text-right">
                  {catPct}%
                </span>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {cat.items.map((item) => (
                <li
                  key={item.code}
                  className="px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-[200px]">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    {item.progression && (
                      <span className="ml-2 inline-block text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5 align-middle">
                        {PROGRESSION_LABEL[item.progression]}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {SELF_LEVELS.map((lvl) => {
                      const active = item.selfAssessment === lvl.value;
                      return (
                        <button
                          key={lvl.value}
                          type="button"
                          title={lvl.label}
                          aria-label={lvl.label}
                          disabled={mutation.isPending}
                          onClick={() =>
                            mutation.mutate({
                              itemCode: item.code,
                              level: active ? null : lvl.value,
                            })
                          }
                          className={`w-8 h-8 rounded-full border text-base leading-none flex items-center justify-center transition disabled:opacity-60 ${
                            active
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'
                          }`}
                        >
                          {lvl.icon}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function sectionStatusClass(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('acqui') || s.includes('valid') || s === 'done')
    return 'bg-green-50 text-green-700 border-green-200';
  if (s.includes('cours') || s.includes('progress'))
    return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

export default function LivretView() {
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'livret'],
    queryFn: fetchLivret,
  });
  const historyQuery = useQuery({
    queryKey: ['candidate', 'history'],
    queryFn: fetchHistory,
  });

  const sections = data?.sections ?? [];
  const lessons = data?.lessons ?? [];
  const bilans = historyQuery.data ?? [];
  const empty =
    !isLoading &&
    !historyQuery.isLoading &&
    sections.length === 0 &&
    lessons.length === 0 &&
    bilans.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mon livret</h1>
        <p className="text-slate-600">
          Votre progression de formation et vos séances enregistrées.
        </p>
      </div>

      <LearningBookletCard />

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center text-slate-500 text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
        </div>
      ) : empty ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 shadow-sm text-center">
          <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4 mx-auto">
            <BookOpen className="text-indigo-600" size={26} />
          </div>
          <p className="text-sm text-slate-500">
            Votre livret ne contient pas encore de données. Il se remplira au fil
            de votre formation.
          </p>
        </div>
      ) : (
        <>
          {sections.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-slate-900 mb-4">Progression</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sections.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3"
                  >
                    <span className="text-sm text-slate-700 truncate">
                      {s.type}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full border text-xs font-semibold flex-shrink-0 ${sectionStatusClass(
                        s.status,
                      )}`}
                    >
                      {s.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bilans.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquareText className="text-indigo-600" size={18} />
                <h2 className="text-slate-900">Retours de vos moniteurs</h2>
              </div>
              <div className="space-y-4">
                {bilans.map((b, i) => (
                  <div key={b.id}>
                    {i > 0 && <div className="border-t border-slate-100 mb-4" />}
                    <div className="flex items-start gap-4">
                      <div className="relative overflow-hidden w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {monitorInitials(b.instructorName)}
                        <Photo url={b.instructorPhotoUrl} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="text-sm font-semibold text-slate-900">
                            {dayjs(b.date).format('ddd D MMM YYYY')}
                          </span>
                          {b.instructorName && (
                            <span className="text-xs text-slate-500">
                              {b.instructorName}
                            </span>
                          )}
                          {b.rating != null && (
                            <span className="text-xs text-amber-600 font-medium">
                              {'★'.repeat(b.rating)}
                              <span className="text-slate-300">
                                {'★'.repeat(Math.max(0, 5 - b.rating))}
                              </span>
                            </span>
                          )}
                        </div>
                        {b.summary && (
                          <p className="text-sm text-slate-600 mt-0.5">
                            « {b.summary} »
                          </p>
                        )}
                        {(b.instructorPhone || b.instructorEmail) && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
                            {b.instructorPhone && (
                              <a
                                href={`tel:${b.instructorPhone}`}
                                className="hover:text-indigo-600"
                              >
                                {b.instructorPhone}
                              </a>
                            )}
                            {b.instructorEmail && (
                              <a
                                href={`mailto:${b.instructorEmail}`}
                                className="hover:text-indigo-600"
                              >
                                {b.instructorEmail}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lessons.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-slate-900 mb-4">Séances enregistrées</h2>
              <div className="divide-y divide-slate-100">
                {lessons.map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center gap-4 py-3.5 first:pt-0"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Car className="text-indigo-600" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">
                        {dayjs(l.startsAt).format('ddd D MMM YYYY · HH:mm')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {l.type ?? 'Séance'} · {Math.round(l.durationMinutes)} min
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
